import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { AgendaTopicAPIData } from '../types/growth-api';
import { QueryKey } from '../types/growth-query-key';
import { useInvalidateGoalById } from './use-goals';

// Types
interface AgendaTopicAssociatedResponse {
	associated: boolean;
	agenda_topics: AgendaTopicAPIData[];
}

interface CreateAgendaTopicFromEntityResponse {
	agenda_topic: AgendaTopicAPIData;
}

interface CreateAgendaTopicFromEntityRequest {
	entityType: string;
	entityId: string;
	meetingId?: string;
}

interface AgendaTopicAssociatedParams {
	entityType: string;
	entityId: string;
	meetingId?: string;
	enabled?: boolean;
}

// Local query keys
enum LocalQueryKey {
	AgendaTopicAssociated = 'agendaTopicAssociated',
}

// Hook to check if an entity is associated with a 1:1 agenda topic
export const useAgendaTopicAssociated = ({ entityType, entityId, meetingId, enabled = true }: AgendaTopicAssociatedParams) => {
	const http = useHttpClient('v2');

	return useQuery<AgendaTopicAssociatedResponse>(
		[LocalQueryKey.AgendaTopicAssociated, entityType, entityId, meetingId],
		async () => {
			const params: Record<string, string> = {
				entity_type: entityType,
				entity_id: entityId,
			};

			if (meetingId) {
				params.meeting_id = meetingId;
			}

			const res = await http.get<AgendaTopicAssociatedResponse>('/one_on_ones/agenda_topics/associated', {
				params,
			});
			return res.data;
		},
		{
			enabled: enabled && !!entityType && !!entityId,
			refetchOnWindowFocus: false,
		}
	);
};

// Hook to create an agenda topic from an entity (V1)
export const useAgendaTopicFromEntity = () => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();
	const invalidateGoalById = useInvalidateGoalById();

	return useMutation(
		async ({ entityType, entityId, meetingId }: CreateAgendaTopicFromEntityRequest) => {
			const payload: Record<string, string> = {
				entity_type: entityType,
				entity_id: entityId,
			};

			if (meetingId) {
				payload.meeting_id = meetingId;
			}

			const res = await http.post<CreateAgendaTopicFromEntityResponse>('/one_on_ones/agenda_topics/from_entity', payload);
			return res.data;
		},
		{
			onSuccess: (data, variables) => {
				// Invalidate all meeting-related queries to show the new agenda topic immediately
				// We use broad invalidation since we don't always know the specific meeting ID
				queryClient.invalidateQueries([QueryKey.CurrentOneOnOne]); // Current 1:1 meetings
				queryClient.invalidateQueries([QueryKey.Meetings]); // Meeting lists
				queryClient.invalidateQueries([QueryKey.Meeting]); // All individual meetings

				// Also invalidate the agenda topic association queries to update UI states
				queryClient.invalidateQueries([LocalQueryKey.AgendaTopicAssociated]);
				if (variables.entityType === 'Growth::Goal') {
					invalidateGoalById(variables.entityId);
				}
			},
		}
	);
};

// V2 Types
interface AppendGoalToV2MeetingResponse {
	success: boolean;
	message: string;
	meeting_id?: string;
	other_participant_name?: string;
}

interface AppendGoalToV2MeetingRequest {
	goalId: string;
}

// Hook to append a goal to the V2 meeting's collaborative document (V2 equivalent of "Discuss in 1:1")
export const useAppendGoalToV2Meeting = () => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();
	const invalidateGoalById = useInvalidateGoalById();

	return useMutation(
		async ({ goalId }: AppendGoalToV2MeetingRequest) => {
			const res = await http.post<AppendGoalToV2MeetingResponse>('/one_on_ones/meetings_v2/append_goal', {
				goal_id: goalId,
			});
			return res.data;
		},
		{
			onSuccess: (data, variables) => {
				// Invalidate V2 meeting queries to reflect the document update
				queryClient.invalidateQueries([QueryKey.MeetingV2]);
				// Also invalidate the goal to update UI states
				invalidateGoalById(variables.goalId);
			},
		}
	);
};
